import { useTranslation } from 'react-i18next';

import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from '@/components/Shared/Table';
import type { OvertimeMonthDto } from '@/types';
import { formatHours, formatSignedHours } from './overtimeFormat';

const MONTH_KEYS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
] as const;

interface OvertimeMonthsTableProps {
  months: OvertimeMonthDto[];
}

/**
 * Schneidet die Monate vor der ersten Bewegung ab.
 * why: der Server liefert bewusst jeden Monat des Fensters, damit die Fortschreibung
 * lückenlos ist — bei einem Konto, das erst spät startet, wären das aber bis zu 60
 * Nullzeilen vor dem ersten echten Wert. Der letzte Monat bleibt immer stehen, damit
 * die Tabelle nie leer ist.
 */
function trimLeadingEmptyMonths(months: OvertimeMonthDto[]): OvertimeMonthDto[] {
  const firstMoved = months.findIndex((m) => m.deltaHours !== 0 || m.closingBalanceHours !== 0);
  return firstMoved === -1 ? months.slice(-1) : months.slice(firstMoved);
}

/** Die monatliche Fortschreibung des Kontos, neuester Monat zuerst. */
export function OvertimeMonthsTable({ months }: OvertimeMonthsTableProps) {
  const { t } = useTranslation('timeclock');

  const rows = [...trimLeadingEmptyMonths(months)].reverse();

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeader>{t('overtime.table.month')}</TableHeader>
          <TableHeader className="hidden md:table-cell">{t('overtime.table.worked')}</TableHeader>
          <TableHeader className="hidden lg:table-cell">{t('overtime.table.credited')}</TableHeader>
          <TableHeader className="hidden md:table-cell">{t('overtime.table.target')}</TableHeader>
          <TableHeader className="hidden lg:table-cell">
            {t('overtime.table.compensation')}
          </TableHeader>
          <TableHeader className="hidden lg:table-cell">
            {t('overtime.table.adjustment')}
          </TableHeader>
          <TableHeader>{t('overtime.table.delta')}</TableHeader>
          <TableHeader>{t('overtime.table.balance')}</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((month) => (
          <TableRow key={`${month.year}-${month.month}`}>
            <TableCell>
              <span className="text-[13px] font-medium text-slate-900">
                {t(`overtime.months.${MONTH_KEYS[month.month - 1]}`)} {month.year}
              </span>
              {/* Mobil tragen die versteckten Spalten nichts — die Bestandteile hier stapeln. */}
              <div className="text-[11px] text-slate-500 mt-0.5 tabular-nums md:hidden">
                {formatHours(month.workedHours)} / {formatHours(month.targetHours)}
              </div>
            </TableCell>
            <TableCell className="hidden md:table-cell text-[13px] text-slate-700 tabular-nums">
              {formatHours(month.workedHours)}
            </TableCell>
            <TableCell className="hidden lg:table-cell text-[13px] text-slate-700 tabular-nums">
              {month.creditedHours > 0 ? formatHours(month.creditedHours) : '–'}
            </TableCell>
            <TableCell className="hidden md:table-cell text-[13px] text-slate-700 tabular-nums">
              {formatHours(month.targetHours)}
            </TableCell>
            <TableCell className="hidden lg:table-cell text-[13px] text-slate-700 tabular-nums">
              {month.compensationHours > 0 ? formatHours(month.compensationHours) : '–'}
            </TableCell>
            <TableCell className="hidden lg:table-cell text-[13px] text-slate-700 tabular-nums">
              {month.adjustmentHours !== 0 ? formatSignedHours(month.adjustmentHours) : '–'}
            </TableCell>
            <TableCell
              className={`text-[13px] tabular-nums ${
                month.deltaHours < 0 ? 'text-red-600' : 'text-slate-700'
              }`}
            >
              {formatSignedHours(month.deltaHours)}
            </TableCell>
            <TableCell
              className={`text-[13px] font-medium tabular-nums ${
                month.closingBalanceHours < 0 ? 'text-red-600' : 'text-slate-900'
              }`}
            >
              {formatSignedHours(month.closingBalanceHours)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
